import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Trophy, 
  Users,
  ArrowRight,
  Star,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [openPlaySessions, setOpenPlaySessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [bookingsRes, eventsRes, openPlayRes] = await Promise.all([
          fetch(`${API}/bookings`, { headers, credentials: 'include' }),
          fetch(`${API}/events`),
          fetch(`${API}/open-play`)
        ]);

        if (bookingsRes.ok) {
          const data = await bookingsRes.json();
          setBookings(data.filter(b => b.status === 'confirmed').slice(0, 3));
        }
        
        if (eventsRes.ok) {
          const data = await eventsRes.json();
          setEvents(data.slice(0, 3));
        }
        
        if (openPlayRes.ok) {
          const data = await openPlayRes.json();
          setOpenPlaySessions(data.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const getSkillBadgeClass = (skill) => {
    switch (skill?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-blue-100 text-blue-800';
      case 'advanced': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = [
    { 
      label: 'Skill Level', 
      value: user?.skill_level || 'Beginner', 
      icon: TrendingUp, 
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    { 
      label: 'Membership', 
      value: user?.membership_type ? 'Active' : 'Free', 
      icon: Star, 
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10'
    },
    { 
      label: 'Bookings', 
      value: bookings.length, 
      icon: Calendar, 
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">
            Welcome back, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-muted-foreground">Ready for your next game?</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Button 
            onClick={() => navigate('/book/venue_camsur')} 
            className="h-auto py-4 flex flex-col items-center justify-center btn-primary"
            data-testid="quick-book-court"
          >
            <MapPin className="h-6 w-6 mb-2" />
            <span>Book Court</span>
          </Button>
          <Button 
            onClick={() => navigate('/open-play')} 
            variant="outline"
            className="h-auto py-4 flex flex-col items-center justify-center"
            data-testid="quick-open-play"
          >
            <Users className="h-6 w-6 mb-2" />
            <span>Open Play</span>
          </Button>
          <Button 
            onClick={() => navigate('/events')} 
            variant="outline"
            className="h-auto py-4 flex flex-col items-center justify-center"
            data-testid="quick-events"
          >
            <Trophy className="h-6 w-6 mb-2" />
            <span>Events</span>
          </Button>
          {/* <Button 
            onClick={() => navigate('/rewards')} 
            variant="outline"
            className="h-auto py-4 flex flex-col items-center justify-center"
            data-testid="quick-rewards"
          >
            <Gift className="h-6 w-6 mb-2" />
            <span>Rewards</span>
          </Button> */}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((stat, index) => (
            <Card key={index} className="card-hover" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-5">
                <div className={`inline-flex p-2 rounded-lg ${stat.bgColor} mb-3`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <p className="text-2xl font-heading font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Upcoming Bookings */}
          <Card className="lg:col-span-3">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Upcoming Bookings</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/my-bookings')}>
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map(i => (
                    <div key={i} className="h-20 bg-muted animate-pulse rounded-lg"></div>
                  ))}
                </div>
              ) : bookings.length > 0 ? (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.booking_id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg" data-testid={`booking-${booking.booking_id}`}>
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Calendar className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{booking.venue_name}</p>
                          <p className="text-sm text-muted-foreground flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {booking.date} at {booking.start_time}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        {booking.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-4">No upcoming bookings</p>
                  <Button onClick={() => navigate('/book/venue_camsur')} className="btn-primary">
                    Book a Court
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loyalty Progress - Hidden */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center">
                <Gift className="h-5 w-5 mr-2 text-secondary" />
                Loyalty Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <p className="text-4xl font-heading font-bold text-primary">{user?.loyalty_points || 0}</p>
                <p className="text-sm text-muted-foreground">Total Points</p>
              </div>
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => navigate('/rewards')}
                  data-testid="view-rewards-btn"
                >
                  View All Rewards
                </Button>
              </div>
            </CardContent>
          </Card> */}
        </div>

        {/* Open Play & Events */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Open Play Sessions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Open Play Sessions</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/open-play')}>
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {openPlaySessions.length > 0 ? (
                <div className="space-y-3">
                  {openPlaySessions.map((session) => (
                    <div key={session.session_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`session-${session.session_id}`}>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <Badge className={getSkillBadgeClass(session.skill_level)}>
                            {session.skill_level}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {session.date} • {session.start_time}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{session.current_players}/{session.max_players}</p>
                        <p className="text-xs text-muted-foreground">players</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">No sessions available</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Upcoming Events</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {events.length > 0 ? (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div key={event.event_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`event-${event.event_id}`}>
                      <div>
                        <p className="font-medium text-sm">{event.name}</p>
                        <p className="text-sm text-muted-foreground">{event.date}</p>
                      </div>
                      <Badge variant="outline">{event.event_type}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">No upcoming events</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
